import type { Dehydrate } from 'vue-model';
import { DoWhatYouWantAction, NodesSaveAction, NodesMoveAction } from '../actions';
import { Node, Board, Slide, Group } from '../models';
import { ref } from 'vue';
import { v4 } from 'uuid';

export function paste(copied: Dehydrate<typeof Node>[], board: Board) {
  board.currentSlide?.nodes.forEach((n) => {
    n.selected = false;
  });

  const setBoardId = (nodes: Dehydrate<typeof Node>[]) => {
    nodes.forEach((node) => {
      if ('nodes' in node) {
        setBoardId(node.nodes as any);
      }
      node.boardId = board._id;
    });
  };

  setBoardId(copied);

  const nodes = copied.map((values) => Node.hydrate(values));
  if (!nodes?.length) return;

  nodes.forEach((n) => {
    n.selected = true;
  });

  const action = new DoWhatYouWantAction<{ nodes: Node[]; slide: Slide }>().start();
  action
    .execute({ nodes, slide: board.currentSlide as Slide }, ({ nodes, slide }) => {
      nodes.forEach((n) => {
        slide.addNode(n);
      });
    })
    .then(() => new NodesSaveAction().execute({ nodes }, undefined, 1))
    .then(() => {
      new NodesMoveAction().execute({ nodes }, 20, 20);
    })
    .then(() => {
      action.stop();
      board.copied = nodes.map((n) => {
        return n.dehydrateAsCopy();
      });
    });
}

export const showConfirmationDialog = ref(false);
export const showPromptDialog = ref(false);
export const showAlertDialog = ref(false);
export const dialogMessage = ref<string | null>(null);
export const yesNoConfirmation = ref(false);
let resolvePromise: ((value: any) => void) | null = null;
let rejectPromise: ((reason?: any) => void) | null = null;
let confirmCallback: ((yesOrNo?: any) => void) | null = null;

export const confirm = (msg: string, cb = (yesOrNo?: boolean): boolean | void => yesOrNo, yesNo = false) => {
  confirmCallback = cb;
  dialogMessage.value = msg;
  yesNoConfirmation.value = yesNo;
  showConfirmationDialog.value = true;

  return new Promise<any>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
};

export const dialogOk = (value: any) => {
  return Promise.resolve(confirmCallback?.(value)).then(resolvePromise, rejectPromise);
};

export const prompt = (msg: string, cb = (value?: string) => value) => {
  confirmCallback = cb;
  dialogMessage.value = msg;
  showPromptDialog.value = true;

  return new Promise<string | undefined>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
};

export const alert = (msg: string) => {
  dialogMessage.value = msg;
  showAlertDialog.value = true;
};

export function adaptNodeStructureForHydration(node: Dehydrate<typeof Slide | typeof Group>) {
  const change = (parent: Dehydrate<typeof Slide | typeof Group> & { children?: Node[] }) => {
    if (parent.order) return;

    parent.nodesCache =
      (parent.nodes || parent.children)?.map((node) => {
        node._id = v4();
        // node.parentId = node._id
        if (node.type === 'Group') {
          change(node as unknown as Dehydrate<typeof Group>);
        }
        node.parentId = node._id;
        return node;
      }) ?? [];

    parent.order = ((parent.nodes || parent.children) as Node[])?.map((node) => node._id) ?? [];
    delete parent.nodes;
    delete parent.children;
  };

  change(node);
}

export const copyNodes = (nodes: Node[], board: Board) => {
  board.copied = nodes.map((n) => {
    return n.dehydrateAsCopy();
  });

  navigator.clipboard.writeText(JSON.stringify(board.copied));
};

export const loadImage = (src: string) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new window.Image();
    img.onload = (_ev: Event) => {
      resolve(img);
    };
    img.onerror = (ev: string | Event) => {
      reject(ev);
    };
    img.src = src;
  });
};

export const scaleImage = async (src: string) => {
  const img = await loadImage(src);

  // Set src based on if it's a .gif or not
  const isGif = src.startsWith('data:image/gif;')

  // If it's a .gif, no need to go further.
  if(isGif){ 
    return img.src;
  } 

  const width = img.naturalWidth;
  const height = img.naturalHeight;

  const ratio = width / height;

  const maxWidth = 1000;
  const maxHeight = 735;

  const needsScale = width > maxWidth || height > maxHeight;

  if (!needsScale && src.startsWith('data:image/webp;')) {
    return src;
  }

  let newWidth = maxWidth;
  let newHeight = maxHeight;

  if (ratio > 1) {
    newHeight = maxWidth / ratio;
  } else {
    newWidth = maxHeight * ratio;
  }

  const canvas = document.createElement('canvas');
  canvas.width = needsScale ? newWidth : width;
  canvas.height = needsScale ? newHeight : height;

  const ctx = canvas.getContext('2d');
  ctx?.drawImage(img, 0, 0, width, height, 0, 0, needsScale ? newWidth : width, needsScale ? newHeight : height);

  // const imgType = img.outerHTML.split(';')[0].slice(21)

  return canvas.toDataURL('image/webp');
};
